import {
  activityCategoryDictionary,
  allocateShiftsToActivityOccurrence,
  deallocateShiftsFromActivityOccurrence,
  useActivity,
  useActivityOccurrence,
} from "@/api/Activities";
import { useNavigate, useParams } from "react-router";
import { z } from "zod";
import {
  activityRequirementSchema,
  getUnfulfilledRequirements,
} from "@medoma/models/activityRequirements";
import { deducedError, useErrorMessageAlert } from "@/Utils/ErrorUtils";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { Loading } from "@medoma/components/Loading/Loading";
import { Trans, useLingui } from "@lingui/react/macro";
import { CloseButton } from "@/components/NavigationButtons/NavigationButtons";
import { CategoryIcon } from "@/components/CategoryIcon/CategoryIcon";
import { DetailsPanelHeader } from "@/components/DetailsPanel/Header";
import { Heading } from "@medoma/components/Heading/Heading";
import styles from "./ActivityOccurrence.module.scss";
import { Tab, TabList, TabPanel, Tabs } from "@medoma/components/Tabs";
import { useState } from "react";
import { StatusTagWithDropdown } from "@/components/StatusTagWithDropdown/StatusTagWithDropdown";
import { AssignedShiftChip } from "@/components/Chips/AssignedShiftChip";
import { RequirementChip } from "@/components/Chips/RequirementChip";
import WarningChip from "@/components/Chips/WarningChip";
import { ShiftDropdownMenu } from "@/pages/commandcenter/Activities/ShiftDropdownMenu";
import { ActivityOccurrenceInformation } from "@/pages/commandcenter/Patients/Patient/Activities/ActivityOccurrence/ActivityOccurrenceInformation";
import { CloseActivitySeriesButton } from "@/pages/commandcenter/Patients/Patient/Activities/ActivityOccurrence/CloseActivitySeriesButton";
import { EditButton } from "@/pages/commandcenter/Patients/Patient/Activities/ActivityOccurrence/EditButton";
import { EditContainer } from "@/pages/commandcenter/Patients/Patient/Activities/ActivityOccurrence/EditContainer";
import { EditNote } from "@/pages/commandcenter/Patients/Patient/Activities/ActivityOccurrence/EditNote";
import {
  activityOccurrenceStatusSchema,
  type IActivityOccurrence,
  categorySchema,
  getActivityCategoryConfig,
  getActivityOccurrenceStatusTimestamp,
  groupStatusSchema,
  isEmployeePerformed,
  hasGroupableCategory,
  type IGroupStatus,
} from "@medoma/models/activities";
import { startOfDay } from "date-fns";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Text, TextWithLineBreaks } from "@medoma/components/Text/Text";
import { PatientHeader } from "@/components/PatientHeader/PatientHeader";
import { RemoveActivityOccurrence } from "@/pages/commandcenter/Patients/Patient/Activities/ActivityOccurrence/RemoveActivityOccurrence";
import { isUnfinishedOccurrence } from "@/forms/changeGroupUtils";
import { ChangeGroup } from "@/forms/ChangeGroup";
import ArrowLeftCurveIcon from "@medoma/components/icons/ArrowLeftCurveIcon";
import { useOccurrenceInvoicingCodes } from "@/api/Invoicing";
import type { NavigationButtons } from "@/components/DetailsPanel/Header";
import { organizationUsesInvoicingCodes } from "@medoma/models/organizations";
import { useUserProfile } from "@/api/Users";
import { useIsGroupedCategory } from "@/api/ActivityGrouping";

const OccurrenceWarningChips = ({
  activityOccurrence,
  visitStatus,
}: {
  activityOccurrence: IActivityOccurrence;
  visitStatus: IGroupStatus | undefined;
}) => {
  const isPartOfCancelledVisit =
    visitStatus === groupStatusSchema.enum.cancelled;

  const performedByEmployee = isEmployeePerformed(activityOccurrence);
  const assignees = performedByEmployee ? activityOccurrence.assignees : [];
  const unfulfilledRequirements = performedByEmployee
    ? getUnfulfilledRequirements({
        assignees,
        requiredCompetences: activityOccurrence.requiredCompetences,
        doubleStaffing:
          activityOccurrence.category === categorySchema.enum.HomeVisit &&
          activityOccurrence.doubleStaffing,
      })
    : [];

  const isFinished =
    activityOccurrence.status === activityOccurrenceStatusSchema.enum.finished;
  const isUnplanned =
    activityOccurrence.status === activityOccurrenceStatusSchema.enum.notReady;
  const isExpired =
    activityOccurrence.status === activityOccurrenceStatusSchema.enum.expired;

  const hasDoubleStaffingUnfulfilled = unfulfilledRequirements.includes(
    activityRequirementSchema.enum.DoubleStaffing,
  );
  const otherUnfulfilled = unfulfilledRequirements.filter(
    (requirement) =>
      requirement !== activityRequirementSchema.enum.DoubleStaffing,
  );
  const showDoubleStaffingWarning = hasDoubleStaffingUnfulfilled && !isFinished;
  const showUnfulfilledRequirementsWarning =
    otherUnfulfilled.length > 0 &&
    !isFinished &&
    !isUnplanned &&
    !isPartOfCancelledVisit;
  const showExpiredWarning =
    isExpired && !isFinished && !isUnplanned && !isPartOfCancelledVisit;
  const showWarnings =
    showDoubleStaffingWarning ||
    showUnfulfilledRequirementsWarning ||
    showExpiredWarning;

  if (!showWarnings) {
    return null;
  }

  return (
    <div className={styles.warningChipsWrapper}>
      {showUnfulfilledRequirementsWarning ? (
        <WarningChip size="small">
          <Trans>Kompetensbehov uppfylls inte</Trans>
        </WarningChip>
      ) : null}
      {showDoubleStaffingWarning ? (
        <WarningChip size="small">
          <Trans>2 medarbetare krävs</Trans>
        </WarningChip>
      ) : null}
      {showExpiredWarning ? (
        <WarningChip size="small">
          <Trans>Ej genomförd</Trans>
        </WarningChip>
      ) : null}
    </div>
  );
};

const OccurrenceStatusAndAssignees = ({
  activityId,
  occurrenceId,
  activityOccurrence,
}: {
  activityId: string;
  occurrenceId: string;
  activityOccurrence: IActivityOccurrence;
}) => {
  const queryClient = useQueryClient();
  const { displayErrorMessageAlert } = useErrorMessageAlert();
  const { t } = useLingui();
  const isGroupedCategory = useIsGroupedCategory();

  const { mutate: allocateShiftsMutation, isPending: isAllocating } =
    useMutation({
      mutationFn: ({ shiftIds }: { shiftIds: number[] }) =>
        allocateShiftsToActivityOccurrence(shiftIds, activityId, occurrenceId),
      onError: (err) => {
        displayErrorMessageAlert(
          `${t`Det gick inte att lägga till arbetspasset på aktiviteten.`} ${deducedError(err)}`,
        );
      },
      onSuccess: () => queryClient.invalidateQueries(),
    });

  const { mutate: deallocateShiftsMutation, isPending: isDeallocating } =
    useMutation({
      mutationFn: ({ shiftIds }: { shiftIds: number[] }) =>
        deallocateShiftsFromActivityOccurrence(
          shiftIds,
          activityId,
          occurrenceId,
        ),
      onError: (err) => {
        displayErrorMessageAlert(
          `${t`Det gick inte att ta bort arbetspasset från aktiviteten.`} ${deducedError(err)}`,
        );
      },
      onSuccess: () => queryClient.invalidateQueries(),
    });

  const performedByEmployee = isEmployeePerformed(activityOccurrence);
  const assignees = performedByEmployee ? activityOccurrence.assignees : [];
  const unfulfilledRequirements = performedByEmployee
    ? getUnfulfilledRequirements({
        assignees,
        requiredCompetences: activityOccurrence.requiredCompetences,
        doubleStaffing:
          activityOccurrence.category === categorySchema.enum.HomeVisit &&
          activityOccurrence.doubleStaffing,
      })
    : [];

  const isFinished =
    activityOccurrence.status === activityOccurrenceStatusSchema.enum.finished;
  // A grouped occurrence's assignees are the group's, managed there.
  const isStandalone = isGroupedCategory(activityOccurrence.category) === false;
  const canManageAssignees = !isFinished && isStandalone;

  return (
    <div className={styles.statusAndAssignees}>
      <StatusTagWithDropdown
        status={activityOccurrence.status}
        variant={
          getActivityOccurrenceStatusTimestamp(activityOccurrence)
            ? "icon-with-timestamp"
            : "icon-and-text"
        }
        timestamp={getActivityOccurrenceStatusTimestamp(activityOccurrence)}
        activityOccurrence={activityOccurrence}
        activityOccurrenceIsPartOfFinishedVisit={false}
      />
      {performedByEmployee ? (
        <div className={styles.assignees}>
          <ul className={styles.assigneesList}>
            {assignees.map((shift) => (
              <li key={shift.id}>
                <AssignedShiftChip
                  medicalCompetence={shift.competence}
                  shift={shift}
                  size="small"
                  onRemove={
                    canManageAssignees
                      ? () => deallocateShiftsMutation({ shiftIds: [shift.id] })
                      : undefined
                  }
                  disabled={isDeallocating}
                />
              </li>
            ))}
            {unfulfilledRequirements.map((unfulfilledRequirement) => (
              <li key={unfulfilledRequirement}>
                <RequirementChip
                  requirement={unfulfilledRequirement}
                  state="unfulfilled"
                  size="small"
                />
              </li>
            ))}
          </ul>
          {canManageAssignees ? (
            <ShiftDropdownMenu
              assignedShiftIds={assignees.map((shift) => shift.id)}
              unfulfilledRequirements={unfulfilledRequirements}
              onAllocate={(shiftId: number) => {
                allocateShiftsMutation({ shiftIds: [shiftId] });
              }}
              isAllocating={isAllocating}
              shiftsFromDate={startOfDay(activityOccurrence.start)}
              patientTeam={
                activityOccurrence.patient &&
                "team" in activityOccurrence.patient
                  ? activityOccurrence.patient.team
                  : null
              }
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const OccurrenceActions = ({
  activityId,
  occurrenceId,
  activityOccurrence,
  onDeleteOccurrenceSuccess,
}: {
  activityId: string;
  occurrenceId: string;
  activityOccurrence: IActivityOccurrence;
  onDeleteOccurrenceSuccess: () => void;
}) => {
  const queryClient = useQueryClient();
  const { t } = useLingui();
  const isGroupedCategory = useIsGroupedCategory();
  const [showChangeGroupForm, setShowChangeGroupForm] = useState(false);

  const isFinished =
    activityOccurrence.status === activityOccurrenceStatusSchema.enum.finished;
  const isGrouped =
    hasGroupableCategory(activityOccurrence) &&
    isGroupedCategory(activityOccurrence.category) === true;
  const canMoveToAnotherGroup =
    isGrouped && isUnfinishedOccurrence(activityOccurrence);
  const canDeleteOccurrence = !isFinished;
  const showActions = canMoveToAnotherGroup || canDeleteOccurrence;

  if (!showActions) {
    return null;
  }

  return (
    <div className={styles.occurrenceActions}>
      <div className={styles.occurrenceActionButtons}>
        {canDeleteOccurrence ? (
          <RemoveActivityOccurrence
            id={occurrenceId}
            activityId={activityId}
            status={activityOccurrence.status}
            onSuccess={async () => {
              void queryClient.invalidateQueries();
              onDeleteOccurrenceSuccess();
            }}
          />
        ) : null}

        {canMoveToAnotherGroup ? (
          <>
            <EditButton
              attribute="move"
              iconStart={<ArrowLeftCurveIcon />}
              label={t`Flytta till ett annat besök`}
              isEditing={showChangeGroupForm}
              toggleIsEditing={() => setShowChangeGroupForm((prev) => !prev)}
            />
          </>
        ) : null}
      </div>
      {canMoveToAnotherGroup && showChangeGroupForm ? (
        <ChangeGroup
          activityOccurrence={activityOccurrence}
          onSubmitSuccess={() => setShowChangeGroupForm(false)}
        />
      ) : null}
    </div>
  );
};

const OccurrenceNote = ({
  activityId,
  occurrenceId,
  activityOccurrence,
}: {
  activityId: string;
  occurrenceId: string;
  activityOccurrence: IActivityOccurrence;
}) => {
  const { t } = useLingui();
  const [isEditingNote, setIsEditingNote] = useState(false);

  return (
    <section className={styles.informationSection}>
      <div className={styles.editableSection}>
        <Heading level="h3">
          <Trans>Kommentar</Trans>
        </Heading>
        <EditButton
          attribute="edit-note"
          label={activityOccurrence.note ? t`Ändra` : t`Lägg till`}
          isEditing={isEditingNote}
          toggleIsEditing={() => setIsEditingNote((prev) => !prev)}
        />
      </div>
      {isEditingNote ? (
        <EditContainer>
          <EditNote
            activityId={activityId}
            occurrenceId={occurrenceId}
            currentNote={activityOccurrence.note}
            onSuccess={() => setIsEditingNote(false)}
          />
        </EditContainer>
      ) : (
        <TextWithLineBreaks>{activityOccurrence.note}</TextWithLineBreaks>
      )}
    </section>
  );
};

const OccurrenceInformation = ({
  activityId,
  occurrenceId,
}: {
  activityId: string;
  occurrenceId: string;
}) => (
  <ActivityOccurrenceInformation
    activityId={activityId}
    occurrenceId={occurrenceId}
    isRecurringTab={false}
  />
);

export const ActivityOccurrenceRoute = () => {
  const { activityId, occurrenceId } = z
    .object({ activityId: z.string(), occurrenceId: z.string() })
    .parse(useParams());
  const navigate = useNavigate();

  return (
    <ActivityOccurrence
      activityId={activityId}
      occurrenceId={occurrenceId}
      closeActivityOccurrencePanel={() => navigate("..")}
      navigationButtonsInHeader={{
        closeButton: <CloseButton close={() => navigate("..")} />,
      }}
    />
  );
};

/**
 * Invoicing codes for an occurrence of a recurring activity. Read-only — codes
 * are owned by the series and edited via the `Invoicing` section on the Series
 * tab. Edits there propagate to occurrences that have not yet been reported to
 * GVR (backend-handled — see the note above `useActivityInvoicingCodes` /
 * `useOccurrenceInvoicingCodes` in @/api/Invoicing.ts).
 */
const OccurrenceInvoicingCodes = ({
  activityOccurrence,
}: {
  activityOccurrence: IActivityOccurrence;
}) => {
  const { data: occurrenceInvoicingCodes } = useOccurrenceInvoicingCodes(
    activityOccurrence.id,
  );

  return (
    <section className={styles.informationSection}>
      <Heading level="h3">
        <Trans>Faktureringskoder</Trans>
      </Heading>
      {occurrenceInvoicingCodes &&
      (occurrenceInvoicingCodes.procedureCodes.length > 0 ||
        occurrenceInvoicingCodes.productCodes.length > 0) ? (
        <>
          <Text element="span">
            {occurrenceInvoicingCodes.procedureCodes.length > 0 && (
              <>
                <Trans>Åtgärd (KVÅ)</Trans>:
                {occurrenceInvoicingCodes.procedureCodes
                  .map(
                    (procedureCode) =>
                      ` ${procedureCode.code} - ${procedureCode.name}`,
                  )
                  .join(", ")}
              </>
            )}
          </Text>
          <Text element="span">
            {occurrenceInvoicingCodes.productCodes.length > 0 && (
              <>
                <Trans>Produkt:</Trans>
                {occurrenceInvoicingCodes.productCodes
                  .map(
                    (productCode) =>
                      ` ${productCode.code} - ${productCode.name}`,
                  )
                  .join(", ")}
              </>
            )}
          </Text>
        </>
      ) : (
        <Trans>Ingen</Trans>
      )}
      <Text element="p" color="faded">
        <Trans>
          Faktureringskoder kan ändras under fliken &quot;Serie&quot;. Endast
          aktivitetstillfällen som inte har rapporterats till
          faktureringssystemet påverkas.
        </Trans>
      </Text>
    </section>
  );
};

const SeriesTabContent = ({
  activityId,
  occurrenceId,
  onCloseActivitySeriesSuccess,
}: {
  activityId: string;
  occurrenceId: string;
  onCloseActivitySeriesSuccess: () => void;
}) => {
  const queryClient = useQueryClient();
  const { t } = useLingui();
  const { data: activity, isPending, isError, error } = useActivity(activityId);

  if (isPending) {
    return <Loading message={t`Hämtar aktivitet`} />;
  }

  if (isError) {
    return <ErrorMessage message={deducedError(error)} />;
  }

  return (
    <>
      <div className={styles.seriesActions}>
        <CloseActivitySeriesButton
          id={activity.id}
          status={activity.status}
          onSuccess={async () => {
            void queryClient.invalidateQueries();
            onCloseActivitySeriesSuccess();
          }}
        />
      </div>
      <ActivityOccurrenceInformation
        activityId={activityId}
        occurrenceId={occurrenceId}
        isRecurringTab={true}
      />
    </>
  );
};

const OccurrenceTabContent = ({
  activityId,
  occurrenceId,
  activityOccurrence,
  onDeleteOccurrenceSuccess,
  visitStatus,
}: {
  activityId: string;
  occurrenceId: string;
  activityOccurrence: IActivityOccurrence;
  onDeleteOccurrenceSuccess: () => void;
  visitStatus: IGroupStatus | undefined;
}) => {
  const { organization } = useUserProfile();

  const isFinished =
    activityOccurrence.status === activityOccurrenceStatusSchema.enum.finished;
  const activityHasPatient = Boolean(activityOccurrence.patient);
  const { allowsInvoicingCode: activityAllowsInvoicingCode } =
    getActivityCategoryConfig({
      category: activityOccurrence.category,
      activityHasPatient: activityHasPatient,
    });
  const isOccurrenceOfRecurringActivity = activityOccurrence.recurring;
  const showOccurrenceInvoicingSection =
    isOccurrenceOfRecurringActivity &&
    isFinished &&
    activityAllowsInvoicingCode &&
    organizationUsesInvoicingCodes(organization);

  return (
    <div className={styles.occurrenceSections}>
      <OccurrenceWarningChips
        activityOccurrence={activityOccurrence}
        visitStatus={visitStatus}
      />
      <OccurrenceStatusAndAssignees
        activityId={activityId}
        occurrenceId={occurrenceId}
        activityOccurrence={activityOccurrence}
      />
      <OccurrenceActions
        activityId={activityId}
        occurrenceId={occurrenceId}
        activityOccurrence={activityOccurrence}
        onDeleteOccurrenceSuccess={onDeleteOccurrenceSuccess}
      />
      <div className={styles.occurrenceInfo}>
        <OccurrenceNote
          activityId={activityId}
          occurrenceId={occurrenceId}
          activityOccurrence={activityOccurrence}
        />
        {showOccurrenceInvoicingSection ? (
          <OccurrenceInvoicingCodes activityOccurrence={activityOccurrence} />
        ) : null}
        <OccurrenceInformation
          activityId={activityId}
          occurrenceId={occurrenceId}
        />
      </div>
    </div>
  );
};

const ActivityOccurrence = ({
  activityId,
  occurrenceId,
  closeActivityOccurrencePanel,
  navigationButtonsInHeader,
  visitStatus,
}: {
  activityId: string;
  occurrenceId: string;
  closeActivityOccurrencePanel: () => void;
  navigationButtonsInHeader?: NavigationButtons;
  visitStatus?: IGroupStatus;
}) => {
  const {
    data: activityOccurrence,
    isPending,
    isError,
    error,
  } = useActivityOccurrence(activityId, occurrenceId);
  const { t } = useLingui();

  const [activeTab, setActiveTab] = useState<"occurrence" | "series">(
    "occurrence",
  );

  if (isPending) {
    return <Loading message={t`Hämtar aktivitet`} padding={24} />;
  }

  if (isError) {
    return <ErrorMessage message={deducedError(error)} padding={24} />;
  }

  return (
    <article className={styles.activityOccurrence}>
      <DetailsPanelHeader
        icon={
          <CategoryIcon category={activityOccurrence.category} size="small" />
        }
        title={t(activityCategoryDictionary[activityOccurrence.category])}
        navigationButtons={navigationButtonsInHeader}
      />

      {activityOccurrence.patient ? (
        <PatientHeader patient={activityOccurrence.patient} />
      ) : null}

      <div className={styles.title}>
        <Heading level="h2" size="large" weight="regular">
          {activityOccurrence.title}
        </Heading>
      </div>

      {activityOccurrence.recurring ? (
        <Tabs>
          <TabList stateOwner="react">
            <Tab
              onClick={() => setActiveTab("occurrence")}
              isSelected={activeTab === "occurrence"}
            >
              <Trans>Tillfälle</Trans>
            </Tab>
            <Tab
              onClick={() => setActiveTab("series")}
              isSelected={activeTab === "series"}
            >
              <Trans>Serie</Trans>
            </Tab>
          </TabList>
          <TabPanel>
            {activeTab === "occurrence" ? (
              <OccurrenceTabContent
                key={`${activityId}-${occurrenceId}`}
                activityId={activityId}
                occurrenceId={occurrenceId}
                activityOccurrence={activityOccurrence}
                onDeleteOccurrenceSuccess={closeActivityOccurrencePanel}
                visitStatus={visitStatus}
              />
            ) : (
              <SeriesTabContent
                key={`${activityId}`}
                activityId={activityId}
                occurrenceId={occurrenceId}
                onCloseActivitySeriesSuccess={closeActivityOccurrencePanel}
              />
            )}
          </TabPanel>
        </Tabs>
      ) : (
        <OccurrenceTabContent
          key={`${activityId}-${occurrenceId}`}
          activityId={activityId}
          occurrenceId={occurrenceId}
          activityOccurrence={activityOccurrence}
          onDeleteOccurrenceSuccess={closeActivityOccurrencePanel}
          visitStatus={visitStatus}
        />
      )}
    </article>
  );
};

export { ActivityOccurrence };
